import { useState, useCallback } from 'react';
import type { PriceRange } from '@wineryxplorer/shared';
import { ValidationError } from '../ui/ValidationError';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface PriceRangeSelectorProps {
  value?: PriceRange;
  onChange: (value?: PriceRange) => void;
  error?: string | string[];
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
}

const PRICE_RANGE_LABELS = {
  1: '$ - Budget Friendly',
  2: '$$ - Moderate',
  3: '$$$ - Mid-Range',
  4: '$$$$ - Upscale',
  5: '$$$$$ - Luxury',
} as const;

export const PriceRangeSelector = ({ 
  value, 
  onChange, 
  error, 
  disabled = false, 
  loading = false,
  required = false 
}: PriceRangeSelectorProps) => {
  const [localError, setLocalError] = useState<string>('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setLocalError(''); // Clear local validation error
    
    if (!newValue) {
      onChange(undefined);
      return;
    }

    const numValue = Number(newValue);
    
    // Client-side validation
    if (isNaN(numValue)) {
      setLocalError('Price range must be a valid number');
      return;
    }
    
    if (numValue < 1 || numValue > 5) {
      setLocalError('Price range must be between 1 and 5');
      return;
    }
    
    if (!Number.isInteger(numValue)) {
      setLocalError('Price range must be a whole number');
      return;
    }

    onChange(numValue as PriceRange);
  }, [onChange]);

  const displayError = error || localError;
  const hasError = Boolean(displayError);

  return (
    <div className="space-y-3">
      <label 
        htmlFor="priceRange" 
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        Price Range
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <select
          id="priceRange"
          value={value || ''}
          onChange={handleChange}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 bg-gray-50 touch-manipulation transition-colors ${
            hasError
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-info-500 focus:border-info-500'
          } ${
            disabled || loading
              ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
              : 'hover:border-gray-400'
          }`}
          style={{ minHeight: '44px' }} // Ensure minimum touch target size
          aria-invalid={hasError}
          aria-describedby={hasError ? 'priceRange-error' : undefined}
        >
          <option value="">Select price range...</option>
          {Object.entries(PRICE_RANGE_LABELS).map(([num, label]) => (
            <option key={num} value={num}>
              {label}
            </option>
          ))}
        </select>
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <LoadingSpinner size="sm" className="text-gray-400" />
          </div>
        )}
      </div>
      
      <ValidationError 
        error={displayError} 
        className="mt-1"
      />
    </div>
  );
};